import { useState, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { CaretDownThickIcon } from '@yoweb/ui/components/Icon';
import { Interactive } from '@yoweb/ui/components/typography';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { th } from '@yoweb/ui/styles/utils/theme';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var NavButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var item = _ref.item;

  var _useRouter = useRouter(),
      pathname = _useRouter.pathname;

  var _useState = useState(function () {
    var expandedInitial = false;

    if ('nested' in item && item.nested) {
      item.nested.forEach(function (nestedItem) {
        var _nestedItem$activePat;

        if (nestedItem !== null && nestedItem !== void 0 && (_nestedItem$activePat = nestedItem.activePatterns) !== null && _nestedItem$activePat !== void 0 && _nestedItem$activePat.some(function (pattern) {
          return pathname.match(pattern);
        })) {
          expandedInitial = true;
        }
      });
    }

    return expandedInitial;
  }),
      isExpanded = _useState[0],
      setIsExpanded = _useState[1];

  if ('nested' in item && item.nested) {
    return /*#__PURE__*/_jsxs(NestedButtonGroup, {
      ref: ref,
      children: [/*#__PURE__*/_jsxs(StyledButton, {
        $isExpanded: isExpanded,
        onClick: function onClick() {
          return setIsExpanded(!isExpanded);
        },
        "data-testid": "".concat(item.testId, "-large"),
        children: [isExpanded ? item.iconActive : item.icon, /*#__PURE__*/_jsx(Interactive, {
          size: "xl",
          children: item.name
        }), /*#__PURE__*/_jsx(motion.span, {
          initial: {
            height: 16,
            width: 16
          },
          animate: {
            rotate: isExpanded ? 180 : 0
          },
          transition: {
            type: 'linear',
            duration: 0.2
          },
          children: /*#__PURE__*/_jsx(CaretDownThickIcon, {
            size: 16
          })
        })]
      }), isExpanded && /*#__PURE__*/_jsx(NestedButtonHolder, {
        children: item.nested.map(function (nestedItem) {
          if ('url' in nestedItem) {
            var _nestedItem$activePat2;

            return /*#__PURE__*/_jsx(Link, {
              href: nestedItem.url,
              prefetch: nestedItem.prefetch,
              passHref: true,
              legacyBehavior: true,
              children: /*#__PURE__*/_jsx(SubButton, {
                as: "a",
                target: nestedItem.target,
                $isActive: (_nestedItem$activePat2 = nestedItem.activePatterns) === null || _nestedItem$activePat2 === void 0 ? void 0 : _nestedItem$activePat2.some(function (pattern) {
                  return pathname.match(pattern);
                }),
                "data-testid": "".concat(nestedItem.testId, "-large"),
                children: /*#__PURE__*/_jsx(Interactive, {
                  size: "md",
                  children: nestedItem.name
                })
              })
            }, nestedItem.name);
          }

          if ('action' in nestedItem) {
            return /*#__PURE__*/_jsx(SubButton, {
              onClick: nestedItem.action,
              "data-testid": "".concat(nestedItem.testId, "-large"),
              children: /*#__PURE__*/_jsx(Interactive, {
                size: "md",
                children: nestedItem.name
              })
            }, nestedItem.name);
          }
        })
      })]
    });
  }

  if ('url' in item) {
    var _item$activePatterns;

    var isActive = (_item$activePatterns = item.activePatterns) === null || _item$activePatterns === void 0 ? void 0 : _item$activePatterns.some(function (pattern) {
      return pathname.match(pattern);
    });
    return /*#__PURE__*/_jsx("div", {
      ref: ref,
      children: /*#__PURE__*/_jsx(Link, {
        href: item.url,
        passHref: true,
        legacyBehavior: true,
        children: /*#__PURE__*/_jsxs(StyledButton, {
          $isActive: isActive,
          as: "a",
          target: item.target,
          "data-testid": "".concat(item.testId, "-large"),
          children: [isActive ? item.iconActive : item.icon, /*#__PURE__*/_jsx(Interactive, {
            size: "xl",
            children: item.name
          })]
        })
      })
    });
  }

  return null;
});
NavButton.displayName = 'NavButton';
var StyledButton = styled.button.withConfig({
  componentId: "sc-1nrrple-0"
})(["min-height:", ";padding:0 ", ";display:grid;gap:", ";grid-template-columns:auto 1fr auto;cursor:pointer;place-items:center start;border:none;border-radius:", ";color:", ";background-color:", ";&:hover{background-color:", ";}", " ", ""], th.getSpace('medium2'), th.getSpace('normal1'), th.getSpace('small4'), th.getRadii('small'), th.getColor('stone'), function (_ref2) {
  var $isActive = _ref2.$isActive;
  return $isActive ? th.getColor('sand') : 'transparent';
}, th.getColor('sandLight'), function (_ref3) {
  var $isActive = _ref3.$isActive;
  return $isActive && css(["color:", ";"], th.getColor('charcoal'));
}, function (_ref4) {
  var $isExpanded = _ref4.$isExpanded;
  return $isExpanded && css(["color:", ";"], th.getColor('charcoal'));
});
var NestedButtonHolder = styled.div.withConfig({
  componentId: "sc-1nrrple-1"
})(["display:grid;gap:", ";"], th.getSpace('small2'));
var NestedButtonGroup = styled.div.withConfig({
  componentId: "sc-1nrrple-2"
})(["display:grid;width:100%;"]);
var SubButton = styled.button.withConfig({
  componentId: "sc-1nrrple-3"
})(["min-height:", ";padding:0 ", ";color:", ";background-color:transparent;border:none;border-radius:", ";place-items:center start;display:grid;cursor:pointer;text-align:left;&:hover{background-color:", ";}", ""], pxToRem(37), th.getSpace('normal1'), th.getColor('stone'), th.getRadii('small'), th.getColor('sandLight'), function (_ref5) {
  var $isActive = _ref5.$isActive;
  return $isActive && css(["background-color:", ";color:", ";"], th.getColor('sand'), th.getColor('charcoal'));
});
export { NavButton };